import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageSquare, ArrowRight, Check, Star, ShieldCheck, Sparkles, MapPin } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTABand, StarRating } from "../components/Shared";
import { BUSINESS, SERVICES, TRUST_SIGNALS, AREAS, REVIEWS, GOOGLE } from "../lib/data";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

export default function Home() {
  return (
    <>
      <SEO
        title="Home & Small-Office Cleaning in Hamilton"
        description="Wisp Home Cleaning offers warm, dependable residential and small-office cleaning across Hamilton, Ancaster, Dundas, Waterdown, Binbrook and Burlington. Book your free home visit."
        image="/hero-cleaner.jpg"
      />

      {/* Hero */}
      <section className="relative overflow-hidden px-5 pt-16 pb-20 md:px-10 md:pt-24">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 top-40 h-72 w-72 rounded-full bg-secondary/20 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Hamilton & Surrounding Communities
            </span>
            <h1 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Less stress.<br />
              <span className="font-semibold text-primary">More of what you love.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              At Wisp, we believe a clean home should feel like a weight lifted. We provide
              thoughtful, reliable cleaning tailored to your home, so you can feel comfortable, cared
              for, and free to enjoy more of what matters most.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-button"
                className="group flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-[0_6px_20px_rgb(42,157,143,0.4)] transition-all duration-300 hover:-translate-y-0.5"
              >
                <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" /> Let's Talk
              </a>
              <Link
                to="/contact"
                data-testid="hero-message-button"
                className="group flex items-center justify-center gap-2 rounded-full border-2 border-slate-300 px-8 py-4 font-semibold text-slate-800 transition-all duration-300 hover:border-primary hover:text-primary hover:-translate-y-0.5"
              >
                <MessageSquare className="h-5 w-5" /> Send a Message
              </Link>
            </div>
            <a
              href={GOOGLE.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-rating"
              className="mt-8 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 transition-colors hover:border-primary/40"
            >
              <StarRating />
              <span className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{GOOGLE.rating}</span> from {GOOGLE.count} Google reviews
              </span>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgb(0,0,0,0.08)]">
              <img
                src="/hero-cleaner.jpg"
                alt="Friendly Wisp cleaner in a royal blue polo and black pants"
                className="h-[420px] w-full object-cover object-[50%_25%] lg:h-[520px]"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-slate-900">Free Home Visit</p>
                  <p className="text-sm text-slate-500">Before any pricing</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="border-y border-slate-200 bg-white px-5 py-16 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_SIGNALS.map((t, i) => (
            <motion.div
              key={t.title}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <Check className="h-6 w-6 text-primary" />
              <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{t.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services summary */}
      <section className="px-5 py-24 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">What we do</span>
            <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              We'll take care of the cleaning, so you can take care of what matters most
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Whether it's a larger family home or a busy small office, we tailor every clean to your
              space — thoughtful, reliable, and done with real care.
            </p>
          </div>
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                className="group overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-primary backdrop-blur">
                    <s.icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-heading text-2xl font-medium text-slate-900">{s.title}</h3>
                  <p className="mt-3 text-slate-600">{s.summary}</p>
                  <ul className="mt-5 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 shrink-0 text-primary" /> {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/services"
                    data-testid={`home-service-link-${i}`}
                    className="mt-6 inline-flex items-center gap-1 font-semibold text-primary"
                  >
                    Learn more <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews highlight */}
      <section className="bg-white px-5 py-24 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Reviews</span>
              <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                What our neighbours say
              </h2>
            </div>
            <Link to="/reviews" data-testid="home-reviews-link" className="inline-flex items-center gap-1 font-semibold text-primary">
              Read all reviews <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {REVIEWS.slice(0, 3).map((r, i) => (
              <motion.article
                key={r.name}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                className="rounded-3xl border border-slate-200 bg-background p-7"
              >
                <StarRating count={r.rating} />
                <p className="mt-4 leading-relaxed text-slate-700">"{r.text}"</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-heading font-semibold text-primary">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{r.name}</p>
                    <p className="text-sm text-slate-500">{r.location}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="px-5 py-24 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Service area</span>
            <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Proudly cleaning across Hamilton & area
            </h2>
          </div>
          <div className="mt-12 flex flex-wrap gap-3">
            {AREAS.map((a) => (
              <span
                key={a.name}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-primary hover:text-primary"
              >
                <MapPin className="h-4 w-4 text-primary" /> {a.name}
              </span>
            ))}
          </div>
          <Link to="/areas" data-testid="home-areas-link" className="mt-8 inline-flex items-center gap-1 font-semibold text-primary">
            See all areas we serve <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Life in a clean home */}
      <section className="bg-white px-5 py-24 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">The Wisp feeling</span>
            <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Life in a clean home
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              More time with the people you love, in a space that finally feels calm, comfortable and
              completely yours.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              { src: "/life-sandwich.jpg", alt: "Three generations of a family relaxing together while the kids play in a clean, bright living room", span: "md:col-span-2" },
              { src: "/life-family-inclusive.jpg", alt: "A couple enjoying a relaxed at-home date night in a clean, stylish living room", span: "" },
              { src: "/life-kids.jpg", alt: "Children playing happily in a spotless, sunny living room", span: "" },
              { src: "/life-yoga.jpg", alt: "A woman heading out with her yoga gear, free to enjoy her time thanks to a clean home", span: "md:col-span-2" },
            ].map((img, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                className={`group overflow-hidden rounded-3xl border border-slate-200 ${img.span}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-64 w-full object-cover transition-transform duration-500 group-hover:scale-105 md:h-72"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tagline banner */}
      <section className="relative overflow-hidden">
        <img
          src="/tagline-livingroom.jpg"
          alt="Bright, open, spotless living room"
          loading="lazy"
          decoding="async"
          className="h-[440px] w-full object-cover md:h-[520px]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/35 to-transparent" />
        <div className="absolute inset-0 flex items-center px-6 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl"
          >
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-white/80">Wisp Home Cleaning</p>
            <h2 className="mt-4 font-heading text-4xl font-light leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Clean Home.<br />
              <span className="font-semibold">Happy You.</span>
            </h2>
            <a
              href={BUSINESS.phoneHref}
              data-testid="tagline-call-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-primary shadow-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              <Phone className="h-5 w-5" /> Let's Talk
            </a>
          </motion.div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
